import React, { useState, useMemo } from 'react';
import { X, ChevronRight, Lightbulb, Clock, Users, Loader2, AlertCircle } from 'lucide-react';
import { useI18n } from '../i18n';
import { formatMemberText } from '../locales/member';
import {
    SetupTable,
    SetupBoard,
    SetupCards,
    SetupTokens,
    SetupPlayerArea,
    SetupDice,
    SetupTiles,
    SetupCoins,
    SetupScoreMarkers,
    SetupCentralArea,
    PLAYER_COLORS,
    GAME_THEMES
} from './setup/SetupComponents';

/**
 * GameSetupOverlay - Fullscreen interactive game setup guide
 * Renders AI-generated setup with clickable hotspots and sidebar instructions
 */
const GameSetupOverlay = ({ 
    game, 
    playerCount, 
    skillLevel, 
    onClose,
    setupData = null,
    isLoading = false,
    error = null
}) => {
    const { t } = useI18n();
    const [activeHotspot, setActiveHotspot] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [boardImageLoaded, setBoardImageLoaded] = useState(false);
    const [boardImageError, setBoardImageError] = useState(false);

    // Get theme based on setup data
    const theme = setupData?.theme ? GAME_THEMES[setupData.theme] || {} : {};

    // Get board image URL (from setupData, game's setup_image, or game's box image)
    const getBoardImageUrl = () => {
        // Priority: 1. AI-provided board image, 2. Game's setup_image_url, 3. Game's box image
        if (setupData?.boardImageUrl) return setupData.boardImageUrl;
        if (game?.setup_image_url) return game.setup_image_url;
        if (game?.image_url) return game.image_url;
        return null;
    };

    const boardImageUrl = getBoardImageUrl();
    
    // Check if we have a real setup image (to hide React board components)
    const hasSetupImage = !!(game?.setup_image_url || setupData?.boardImageUrl);

    // Handle hotspot click
    const handleHotspotClick = (hotspotId) => {
        if (activeHotspot === hotspotId) {
            setActiveHotspot(null);
            setSidebarOpen(false);
        } else {
            setActiveHotspot(hotspotId);
            setSidebarOpen(true);
        }
    };

    // Get hotspot info by ID
    const getHotspotInfo = (hotspotId) => {
        return setupData?.hotspots?.find(h => h.id === hotspotId) || null;
    };

    // Close sidebar when clicking outside
    const handleTableClick = (e) => {
        if (e.target === e.currentTarget) {
            setActiveHotspot(null);
            setSidebarOpen(false);
        }
    };

    // Render a component based on type
    const renderComponent = (component, index) => {
        const isActive = activeHotspot === component.hotspotId;
        const commonProps = {
            key: index,
            isHotspot: !!component.hotspotId,
            hotspotId: component.hotspotId,
            isActive,
            onClick: handleHotspotClick,
            theme,
            position: component.position || { x: 50, y: 50 }
        };

        switch (component.type) {
            case 'board':
                // If we have a real setup image, don't render React board - just use the image as background
                if (hasSetupImage) {
                    return null;
                }
                return (
                    <SetupBoard
                        {...commonProps}
                        variant={component.variant || 'overlay'}
                        label={component.label || ''}
                        rows={component.rows || 4}
                        cols={component.cols || 4}
                        size={component.size || { w: 35, h: 25 }}
                    />
                );

            case 'cards':
                return (
                    <SetupCards
                        {...commonProps}
                        variant={component.variant || 'deck'}
                        count={component.count || 1}
                        label={component.label || ''}
                        color={component.color || '#1e3a5f'}
                        faceColor={component.faceColor || '#f5f5f0'}
                        pattern={component.pattern || 'diamond'}
                    />
                );

            case 'tokens':
                return (
                    <SetupTokens
                        {...commonProps}
                        variant={component.variant || 'meeple'}
                        count={component.count || 1}
                        color={component.color || '#e74c3c'}
                        colors={component.colors || null}
                        label={component.label || ''}
                        size={component.size || 24}
                    />
                );

            case 'playerArea':
                // Render multiple player areas
                if (component.count && (component.arrangement === 'around-table' || component.arrangement === 'edge')) {
                    return Array.from({ length: Math.min(component.count, playerCount) }).map((_, i) => (
                        <SetupPlayerArea
                            key={`${index}-player-${i}`}
                            playerNumber={i + 1}
                            playerCount={Math.min(component.count, playerCount)}
                            color={PLAYER_COLORS[i % PLAYER_COLORS.length]}
                            arrangement={component.arrangement || 'edge'}
                            name={component.names?.[i] || ''}
                            isHotspot={!!component.hotspotId}
                            hotspotId={component.hotspotId}
                            isActive={isActive}
                            onClick={handleHotspotClick}
                            theme={theme}
                        />
                    ));
                }
                return (
                    <SetupPlayerArea
                        {...commonProps}
                        playerNumber={component.playerNumber || 1}
                        playerCount={playerCount}
                        color={PLAYER_COLORS[(component.playerNumber - 1) % PLAYER_COLORS.length]}
                        name={component.name || ''}
                    />
                );

            case 'dice':
                return (
                    <SetupDice
                        {...commonProps}
                        count={component.count || 2}
                        sides={component.sides || 6}
                        color={component.color || '#f5f5f5'}
                        dotColor={component.dotColor || '#1a1a1a'}
                        values={component.values}
                        label={component.label || ''}
                        size={component.size || 32}
                    />
                );

            case 'tiles':
                return (
                    <SetupTiles
                        {...commonProps}
                        count={component.count || 1}
                        shape={component.shape || 'square'}
                        color={component.color || '#8e44ad'}
                        label={component.label || ''}
                        size={component.size || 28}
                    />
                );

            case 'coins':
                return (
                    <SetupCoins
                        {...commonProps}
                        variant={component.variant || 'gold'}
                        count={component.count || 1}
                        value={component.value}
                        label={component.label || ''}
                        size={component.size || 22}
                    />
                );

            case 'scoreMarkers':
                return (
                    <SetupScoreMarkers
                        {...commonProps}
                        value={component.value || 0}
                        variant={component.variant || 'chip'}
                        label={component.label || ''}
                        color={component.color || null}
                        size={component.size || 28}
                    />
                );

            case 'centralArea':
                return (
                    <SetupCentralArea
                        {...commonProps}
                        label={component.label || ''}
                        size={component.size || { w: 30, h: 30 }}
                    />
                );

            default:
                return null;
        }
    };

    const skillLabels = useMemo(() => [
        t.skillLevels.beginner,
        t.skillLevels.novice,
        t.skillLevels.hobbyist,
        t.skillLevels.experienced,
        t.skillLevels.veteran,
        t.skillLevels.pro
    ], [t]);

    return (
        <div className="game-setup-overlay">
            {/* Header */}
            <div className="setup-header">
                <div className="setup-header-left">
                    <h2>{game?.name || t.gameHelpChat.gameFallback} {t.gameSetupOverlay.setupTitle}</h2>
                    <div className="setup-meta">
                        <span className="meta-item">
                            <Users size={16} />
                            {playerCount} {t.common.players}
                        </span>
                        <span className="meta-item">
                            <Lightbulb size={16} />
                            {skillLabels[skillLevel] || t.skillLevels.experienced}
                        </span>
                        {setupData?.estimatedTime && (
                            <span className="meta-item">
                                <Clock size={16} />
                                {setupData.estimatedTime}
                            </span>
                        )}
                    </div>
                </div>
                <button className="setup-close-btn" onClick={onClose}>
                    <X size={24} />
                </button>
            </div>

            {/* Main Content */}
            <div className="setup-content">
                {/* Loading State */}
                {isLoading && (
                    <div className="setup-loading">
                        <Loader2 size={48} className="spin" />
                        <p>{t.gameSetupOverlay.generating}</p>
                        <p className="loading-hint">{formatMemberText(t.gameSetupOverlay.analyzingRules, { name: game?.name })}</p>
                    </div>
                )}

                {/* Error State */}
                {error && (
                    <div className="setup-error">
                        <AlertCircle size={48} />
                        <h3>{t.gameSetupOverlay.errorTitle}</h3>
                        <p>{error}</p>
                        <button className="btn-primary" onClick={onClose}>
                            {t.common.close}
                        </button>
                    </div>
                )}

                {/* Setup Table with Board Image Background */}
                {!isLoading && !error && setupData && (
                    <div className="setup-table-container" onClick={handleTableClick}>
                        {/* Visual Board Area */}
                        <div className="setup-board-area">
                            {/* Board Image Background Layer */}
                            {boardImageUrl && !boardImageError && (
                                <div className="board-image-layer">
                                    <img 
                                        src={boardImageUrl}
                                        alt={formatMemberText(t.gameSetupOverlay.gameBoard, { name: game?.name })}
                                        className={`board-background-image ${boardImageLoaded ? 'loaded' : ''}`}
                                        onLoad={() => setBoardImageLoaded(true)}
                                        onError={() => setBoardImageError(true)}
                                    />
                                    <div className="board-image-overlay" />
                                </div>
                            )}

                            <SetupTable 
                                shape={setupData.tableShape || 'rectangle'}
                                theme={theme}
                                style={{
                                    background: boardImageUrl && boardImageLoaded && !boardImageError 
                                        ? 'rgba(0,0,0,0.1)' 
                                        : undefined,
                                    minHeight: '400px'
                                }}
                            >
                                {setupData.components?.map((component, index) => 
                                    renderComponent(component, index)
                                )}
                            </SetupTable>

                            {/* Board Image Credit */}
                            {boardImageUrl && boardImageLoaded && !boardImageError && (
                                <div className="board-image-credit">
                                    📷 {game?.name}
                                </div>
                            )}

                            {/* Game Mode Badge */}
                            {setupData.gameMode && (
                                <div className="game-mode-badge">
                                    🎮 {setupData.gameMode}
                                </div>
                            )}
                        </div>

                        {/* Recommendations */}
                        {setupData.recommendations && (
                            <div className="setup-recommendations">
                                <Lightbulb size={16} />
                                <span>{setupData.recommendations}</span>
                            </div>
                        )}

                        {/* Written Setup Instructions */}
                        {(setupData.setupSteps || setupData.turnStructure) && (
                            <div className="setup-written-instructions">
                                {setupData.setupSteps && (
                                    <div className="instruction-section">
                                        <h3>{t.gameSetupOverlay.preparation}</h3>
                                        <ol className="setup-steps-list">
                                            {setupData.setupSteps.map((step, i) => (
                                                <li key={i}>{step}</li>
                                            ))}
                                        </ol>
                                    </div>
                                )}
                                {setupData.turnStructure && (
                                    <div className="instruction-section">
                                        <h3>{t.gameSetupOverlay.turnStructure}</h3>
                                        <ol className="turn-steps-list">
                                            {setupData.turnStructure.map((step, i) => (
                                                <li key={i}>{step}</li>
                                            ))}
                                        </ol>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Sidebar */}
                <div className={`setup-sidebar ${sidebarOpen ? 'open' : ''}`}>
                    <div className="sidebar-header">
                        <h3>
                            <ChevronRight size={20} />
                            {getHotspotInfo(activeHotspot)?.title || t.gameSetupOverlay.selectTarget}
                        </h3>
                        <button 
                            className="sidebar-close"
                            onClick={() => { setActiveHotspot(null); setSidebarOpen(false); }}
                        >
                            <X size={18} />
                        </button>
                    </div>
                    
                    <div className="sidebar-content">
                        {activeHotspot ? (
                            <>
                                <div className="sidebar-description">
                                    {getHotspotInfo(activeHotspot)?.description || t.gameSetupOverlay.noDescription}
                                </div>
                                
                                {getHotspotInfo(activeHotspot)?.tip && (
                                    <div className="sidebar-tip">
                                        <Lightbulb size={16} />
                                        <span>{getHotspotInfo(activeHotspot).tip}</span>
                                    </div>
                                )}

                                {getHotspotInfo(activeHotspot)?.steps && (
                                    <div className="sidebar-steps">
                                        <h4>{t.gameSetupOverlay.steps}</h4>
                                        <ol>
                                            {getHotspotInfo(activeHotspot).steps.map((step, i) => (
                                                <li key={i}>{step}</li>
                                            ))}
                                        </ol>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="sidebar-empty">
                                <p>{t.gameSetupOverlay.clickHint}</p>
                            </div>
                        )}
                    </div>

                    {/* All Hotspots List */}
                    {setupData?.hotspots && setupData.hotspots.length > 0 && (
                        <div className="sidebar-hotspot-list">
                            <h4>{t.gameSetupOverlay.allParts}</h4>
                            <ul>
                                {setupData.hotspots.map(hotspot => (
                                    <li 
                                        key={hotspot.id}
                                        className={activeHotspot === hotspot.id ? 'active' : ''}
                                        onClick={() => handleHotspotClick(hotspot.id)}
                                    >
                                        <ChevronRight size={14} />
                                        {hotspot.title}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>

            <style>{`
                .game-setup-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: linear-gradient(135deg, #0d1117 0%, #161b22 100%);
                    z-index: 9999;
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                }

                .setup-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 1rem 1.5rem;
                    background: rgba(0,0,0,0.4);
                    border-bottom: 1px solid #30363d;
                }

                .setup-header h2 {
                    margin: 0;
                    color: #fff;
                    font-size: 1.5rem;
                }

                .setup-meta {
                    display: flex;
                    gap: 1rem;
                    margin-top: 0.25rem;
                }

                .meta-item {
                    display: flex;
                    align-items: center;
                    gap: 0.25rem;
                    color: #8b949e;
                    font-size: 0.85rem;
                }

                .setup-close-btn {
                    background: rgba(255,255,255,0.1);
                    border: none;
                    color: #fff;
                    width: 40px;
                    height: 40px;
                    border-radius: 8px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s;
                }

                .setup-close-btn:hover {
                    background: rgba(255,255,255,0.2);
                }

                .setup-content {
                    flex: 1;
                    display: flex;
                    overflow: hidden;
                    position: relative;
                }

                .setup-loading,
                .setup-error {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    color: #fff;
                    gap: 1rem;
                }

                .setup-loading .spin {
                    animation: spin 1s linear infinite;
                }

                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }

                .loading-hint {
                    color: #8b949e;
                    font-size: 0.9rem;
                }

                .setup-error {
                    color: #f85149;
                }

                .setup-error h3 {
                    margin: 0;
                }

                .setup-error p {
                    color: #8b949e;
                }

                .setup-table-container {
                    flex: 1;
                    padding: 3rem 4rem;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: flex-start;
                    position: relative;
                    transition: margin-right 0.3s ease;
                    overflow-y: auto;
                    gap: 1.5rem;
                }

                .setup-sidebar.open ~ .setup-table-container,
                .setup-table-container:has(~ .setup-sidebar.open) {
                    margin-right: 320px;
                }

                /* Board area wrapper */
                .setup-board-area {
                    position: relative;
                    width: 100%;
                    max-width: 900px;
                    aspect-ratio: 16/10;
                    border-radius: 16px;
                    overflow: hidden;
                    box-shadow: 0 8px 32px rgba(0,0,0,0.4);
                }

                /* Board Image Background Layer */
                .board-image-layer {
                    position: absolute;
                    inset: 0;
                    z-index: 0;
                }

                .board-background-image {
                    width: 100%;
                    height: 100%;
                    object-fit: contain;
                    object-position: center;
                    opacity: 0;
                    transition: opacity 0.5s ease;
                    background: #1a1a1a;
                }

                .board-background-image.loaded {
                    opacity: 1;
                }

                .board-image-overlay {
                    position: absolute;
                    inset: 0;
                    background: linear-gradient(
                        135deg,
                        rgba(0,0,0,0.08) 0%,
                        rgba(0,0,0,0.02) 50%,
                        rgba(0,0,0,0.08) 100%
                    );
                    pointer-events: none;
                }

                .board-image-credit {
                    position: absolute;
                    bottom: 0.5rem;
                    right: 0.5rem;
                    background: rgba(0,0,0,0.8);
                    color: #8b949e;
                    font-size: 0.6rem;
                    padding: 0.2rem 0.5rem;
                    border-radius: 4px;
                    z-index: 10;
                }

                .setup-table {
                    position: relative;
                    z-index: 1;
                    width: 100%;
                    height: 100%;
                }

                .game-mode-badge {
                    position: absolute;
                    top: 0.75rem;
                    left: 0.75rem;
                    background: rgba(0,0,0,0.85);
                    color: #fff;
                    padding: 0.4rem 0.8rem;
                    border-radius: 16px;
                    font-size: 0.8rem;
                    border: 1px solid #30363d;
                    z-index: 10;
                }

                .setup-recommendations {
                    width: 100%;
                    max-width: 900px;
                    background: rgba(255,215,0,0.1);
                    border: 1px solid rgba(255,215,0,0.3);
                    color: #ffd700;
                    padding: 0.75rem 1rem;
                    border-radius: 8px;
                    font-size: 0.85rem;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                /* Written Instructions Section */
                .setup-written-instructions {
                    width: 100%;
                    max-width: 900px;
                    background: rgba(30, 35, 40, 0.95);
                    border: 1px solid #30363d;
                    border-radius: 12px;
                    padding: 1.5rem;
                    display: flex;
                    flex-direction: column;
                    gap: 1.5rem;
                }

                .instruction-section h3 {
                    margin: 0 0 0.75rem 0;
                    color: #e6edf3;
                    font-size: 1rem;
                    font-weight: 600;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .setup-steps-list,
                .turn-steps-list {
                    margin: 0;
                    padding-left: 1.5rem;
                    color: #c9d1d9;
                    font-size: 0.9rem;
                    line-height: 1.6;
                }

                .setup-steps-list li,
                .turn-steps-list li {
                    margin-bottom: 0.5rem;
                }

                .setup-steps-list li::marker {
                    color: #58a6ff;
                }

                .turn-steps-list li::marker {
                    color: #3fb950;
                }

                .setup-sidebar {
                    position: absolute;
                    top: 0;
                    right: -320px;
                    width: 320px;
                    height: 100%;
                    background: #161b22;
                    border-left: 1px solid #30363d;
                    display: flex;
                    flex-direction: column;
                    transition: right 0.3s ease;
                    z-index: 10;
                }

                .setup-sidebar.open {
                    right: 0;
                }

                .sidebar-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 1rem;
                    border-bottom: 1px solid #30363d;
                    background: rgba(0,0,0,0.3);
                }

                .sidebar-header h3 {
                    margin: 0;
                    color: #fff;
                    font-size: 1.1rem;
                    display: flex;
                    align-items: center;
                    gap: 0.25rem;
                }

                .sidebar-close {
                    background: none;
                    border: none;
                    color: #8b949e;
                    cursor: pointer;
                    padding: 4px;
                }

                .sidebar-close:hover {
                    color: #fff;
                }

                .sidebar-content {
                    flex: 1;
                    padding: 1rem;
                    overflow-y: auto;
                }

                .sidebar-description {
                    color: #c9d1d9;
                    line-height: 1.6;
                    margin-bottom: 1rem;
                }

                .sidebar-tip {
                    background: rgba(255,215,0,0.1);
                    border: 1px solid rgba(255,215,0,0.3);
                    border-radius: 8px;
                    padding: 0.75rem;
                    display: flex;
                    align-items: flex-start;
                    gap: 0.5rem;
                    color: #ffd700;
                    font-size: 0.9rem;
                    margin-bottom: 1rem;
                }

                .sidebar-steps {
                    background: rgba(0,0,0,0.3);
                    border-radius: 8px;
                    padding: 1rem;
                }

                .sidebar-steps h4 {
                    margin: 0 0 0.5rem 0;
                    color: #fff;
                    font-size: 0.9rem;
                }

                .sidebar-steps ol {
                    margin: 0;
                    padding-left: 1.25rem;
                    color: #c9d1d9;
                }

                .sidebar-steps li {
                    margin-bottom: 0.5rem;
                    line-height: 1.4;
                }

                .sidebar-empty {
                    text-align: center;
                    color: #8b949e;
                    padding: 2rem 1rem;
                }

                .sidebar-hotspot-list {
                    border-top: 1px solid #30363d;
                    padding: 1rem;
                }

                .sidebar-hotspot-list h4 {
                    margin: 0 0 0.75rem 0;
                    color: #8b949e;
                    font-size: 0.8rem;
                    text-transform: uppercase;
                }

                .sidebar-hotspot-list ul {
                    list-style: none;
                    margin: 0;
                    padding: 0;
                }

                .sidebar-hotspot-list li {
                    display: flex;
                    align-items: center;
                    gap: 0.25rem;
                    padding: 0.5rem;
                    color: #c9d1d9;
                    cursor: pointer;
                    border-radius: 6px;
                    transition: all 0.2s;
                }

                .sidebar-hotspot-list li:hover {
                    background: rgba(255,255,255,0.05);
                }

                .sidebar-hotspot-list li.active {
                    background: rgba(255,215,0,0.1);
                    color: #ffd700;
                }

                /* Mobile Responsive */
                @media (max-width: 768px) {
                    .game-setup-overlay {
                        overflow-y: auto;
                    }

                    .setup-header {
                        position: sticky;
                        top: 0;
                        z-index: 100;
                        padding: 0.75rem 1rem;
                    }

                    .setup-header h2 {
                        font-size: 1.1rem;
                    }

                    .setup-meta {
                        gap: 0.5rem;
                        flex-wrap: wrap;
                    }

                    .meta-item {
                        font-size: 0.75rem;
                    }

                    .setup-close-btn {
                        width: 44px;
                        height: 44px;
                        position: fixed;
                        top: 8px;
                        right: 8px;
                        z-index: 200;
                        background: rgba(255,255,255,0.95);
                        color: #333;
                        border-radius: 50%;
                        box-shadow: 0 2px 10px rgba(0,0,0,0.3);
                    }

                    .setup-close-btn:hover {
                        background: #fff;
                    }

                    .setup-content {
                        flex-direction: column;
                        overflow-y: auto;
                    }

                    .setup-table-container {
                        padding: 1rem;
                        padding-bottom: 2rem;
                    }

                    .setup-board-area {
                        aspect-ratio: auto;
                        min-height: 300px;
                    }

                    .setup-sidebar {
                        position: fixed;
                        top: 0;
                        right: -100%;
                        width: calc(100% - 24px);
                        height: 100%;
                        z-index: 150;
                    }

                    .setup-sidebar.open {
                        right: 0;
                    }

                    .setup-written-instructions {
                        padding: 1rem;
                    }

                    .instruction-section h3 {
                        font-size: 0.9rem;
                    }

                    .setup-steps-list,
                    .turn-steps-list {
                        font-size: 0.85rem;
                    }
                }
            `}</style>
        </div>
    );
};

export default GameSetupOverlay;

